// Copyright 2024 Vajra. All rights reserved.
// Settings Page

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  ClipboardDocumentIcon,
  TrashIcon,
  KeyIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { apiClient } from '../lib/api';

interface EnrollmentToken {
  id: string;
  name: string;
  token: string;
  status: 'active' | 'revoked' | 'expired';
  expiresAt: string | null;
  usageCount: number;
  maxUsage: number | null;
  createdAt: string;
}

export default function SettingsPage() {
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [tokenName, setTokenName] = useState('');
  const [copiedTokenId, setCopiedTokenId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['enrollmentTokens'],
    queryFn: () => apiClient.getEnrollmentTokens().then((res) => res.data),
  });

  const createMutation = useMutation({
    mutationFn: (name: string) => apiClient.createEnrollmentToken({ name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enrollmentTokens'] });
      setShowCreateModal(false);
      setTokenName('');
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => apiClient.revokeEnrollmentToken(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enrollmentTokens'] });
    },
  });

  const copyToClipboard = async (token: string, id: string) => {
    await navigator.clipboard.writeText(token);
    setCopiedTokenId(id);
    setTimeout(() => setCopiedTokenId(null), 2000);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'revoked':
        return 'bg-red-100 text-red-800';
      case 'expired':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900">Settings</h1>
      <p className="mt-2 text-sm text-gray-700">
        Configure system settings and integrations.
      </p>

      {/* Enrollment Tokens Section */}
      <div className="mt-8">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h2 className="text-lg font-medium text-gray-900">Enrollment Tokens</h2>
            <p className="mt-1 text-sm text-gray-500">
              Generate tokens to enroll browsers in your organization.
            </p>
          </div>
          <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
            <button
              type="button"
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500"
            >
              <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
              Generate Token
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : (
          <div className="mt-4 flow-root">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                          Name
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Token
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Status
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Expires
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Usage
                        </th>
                        <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {data?.tokens?.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="py-12 text-center">
                            <KeyIcon className="mx-auto h-12 w-12 text-gray-400" />
                            <h3 className="mt-2 text-sm font-medium text-gray-900">No enrollment tokens</h3>
                            <p className="mt-1 text-sm text-gray-500">
                              Generate a token to start enrolling browsers.
                            </p>
                          </td>
                        </tr>
                      ) : (
                        data?.tokens?.map((token: EnrollmentToken) => (
                          <tr key={token.id}>
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                              {token.name}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              <div className="flex items-center gap-2">
                                <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                                  {token.token.substring(0, 16)}...
                                </code>
                                <button
                                  onClick={() => copyToClipboard(token.token, token.id)}
                                  className="text-gray-400 hover:text-gray-600"
                                  title="Copy token"
                                >
                                  {copiedTokenId === token.id ? (
                                    <CheckIcon className="h-4 w-4 text-green-600" />
                                  ) : (
                                    <ClipboardDocumentIcon className="h-4 w-4" />
                                  )}
                                </button>
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm">
                              <span
                                className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${getStatusBadge(
                                  token.status
                                )}`}
                              >
                                {token.status}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              {formatDate(token.expiresAt)}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              {token.usageCount}
                              {token.maxUsage ? ` / ${token.maxUsage}` : ''}
                            </td>
                            <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                              {token.status === 'active' && (
                                <button
                                  onClick={() => revokeMutation.mutate(token.id)}
                                  disabled={revokeMutation.isPending}
                                  className="text-red-600 hover:text-red-900 inline-flex items-center gap-1"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                  Revoke
                                </button>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Registry Key Instructions */}
        <div className="mt-6 rounded-md bg-blue-50 p-4">
          <h3 className="text-sm font-medium text-blue-800">Enrollment Instructions</h3>
          <p className="mt-2 text-sm text-blue-700">
            To enroll browsers using a token, configure the following registry key:
          </p>
          <div className="mt-2 bg-white rounded p-3">
            <code className="text-xs text-gray-800 block">
              HKEY_LOCAL_MACHINE\SOFTWARE\Policies\Vajra\EnrollmentToken
            </code>
          </div>
          <p className="mt-2 text-sm text-blue-700">
            Or use the enrollment URL: <code className="bg-white px-1 rounded">https://enroll.samyoga.solutions/?token=YOUR_TOKEN</code>
          </p>
        </div>
      </div>

      {/* Create Token Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowCreateModal(false)} />
            <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div>
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
                  <KeyIcon className="h-6 w-6 text-blue-600" aria-hidden="true" />
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-base font-semibold leading-6 text-gray-900">
                    Generate Enrollment Token
                  </h3>
                  <div className="mt-4">
                    <label htmlFor="tokenName" className="block text-sm font-medium text-gray-700 text-left">
                      Token Name
                    </label>
                    <input
                      type="text"
                      id="tokenName"
                      value={tokenName}
                      onChange={(e) => setTokenName(e.target.value)}
                      placeholder="e.g., Engineering Team Token"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                <button
                  type="button"
                  onClick={() => createMutation.mutate(tokenName)}
                  disabled={!tokenName.trim() || createMutation.isPending}
                  className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-50 sm:col-start-2"
                >
                  {createMutation.isPending ? 'Creating...' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
